/**
 * Sometimes you want to retry some stuff if it failed on the first try. This
 * module provides some helpers to call a runnable function mor then once.
 * @packageDocumentation
 * @module promises/retry
 * @preferred
 */
import { timeout } from "./timeout.js";
import { constantRetryBackoff } from "./retry_backoff.js";
/**
 * Executes a `runnable` function with a retry and failover-backoff (delay).
 * On success the return value of the runnable function is returned.
 *
 * @typeparam T The return type of the runnable. Is used for the promise.
 * @param runnable - The function to execute
 * @param options - The options to use. Default: `{retries: 3, getRetryBackoff: constantRetryBackoff(5), onRetry: () => {}}`
 * @returns A `Promise` for the result of the runnable.
 * @category Main
 */
export const retryRunnable = async (runnable, options = {}) => {
    let triesSoFar = 0;
    const { getRetryBackoff = constantRetryBackoff(5), onRetry, retries = 3 } = options;
    // we use 'while' instead of 'for' because we definitely need to throw
    // the error after last retry
    /* eslint-disable-next-line */
    while (true) {
        triesSoFar++;
        try {
            return await runnable();
        }
        catch (error) {
            if (triesSoFar > retries) {
                throw error;
            }
            const currentBackoff = getRetryBackoff(triesSoFar, error);
            if (currentBackoff < 0) {
                throw error;
            }
            if (onRetry) {
                await onRetry(error, triesSoFar, currentBackoff);
            }
            await timeout(currentBackoff);
        }
    }
};
